/**
 * Section: Cart drawer
 * -----------------------------------------------------------------------------
 * Contains highly coupled scripts to render cart items onto a drawer.
 *
 * @namespace cartDrawer
 */

import log from 'salvo-lite/log'

import axios from 'axios'
import { register } from '@shopify/theme-sections'
import { formatMoney } from '@shopify/theme-currency'

import cssClasses from '../helpers/cssClasses'
import { imageParameters, getProductTitle, percentageSaving } from '../helpers/utils'

/**
 * Set Axios to no-cache.
 */
axios.defaults.headers.common = {
    Pragma: 'no-cache'
}

class CartDrawer {
    constructor () {
        this._selectors = {
            body: '[js-cart-drawer="body"]',
            footer: '[js-cart-drawer="footer"]',
            subtotal: '[js-ajax-cart="subtotal"]',
            aocBanner: '[js-aoc-banner="container"]',
            klarnaWidget: '[js-ajax-klarna="container"]',
            lifestyleProduct: 'lifestyle_product'
        }

        log.debug(this.constructor.name, 'Constructed', this)
    }

    onInit (theme, section) {
        register('cart-drawer', {

            /**
             * Initialise section.
             */
            init () {
                this.section = theme.getSectionsByType('cart-drawer')
                this._Drawer = theme.getComponentsById('cart-drawer-toggle')
                this.body = document.querySelector(this.section._selectors.body)
                this.footer = document.querySelector(this.section._selectors.footer)
                this.subtotal = document.querySelector(this.section._selectors.subtotal)
                this.aocBanner = document.querySelector(this.section._selectors.aocBanner)
                this.klarnaWidget = document.querySelector(this.section._selectors.klarnaWidget)
                this.freeGiftProducts = JSON.parse(document.querySelector('[data-free-gift-products]').getAttribute('data-free-gift-products'))
            },

            /**
             * Set listeners for each instance.
             */
            setListeners () {
                window.Frame.EventBus.listen('Toggle:cartDrawerTarget:open', () => {
                    this.render()
                    window.Frame.EventBus.emit('SiteHeader:update', 'drawer')
                })

                window.Frame.EventBus.listen('Toggle:cartDrawerTarget:close', () => window.Frame.EventBus.emit('SiteHeader:update'))
                window.Frame.EventBus.listen('AjaxCart:itemAdded', () => this.open())
                window.Frame.EventBus.listen(['AjaxCart:cartCleared', 'AjaxCart:itemRemoved', 'AjaxCart:freeGiftItemRemoved'], () => this.render())
                window.Frame.EventBus.listen(['AjaxCart:removingItem'], (item) => this.setLoadingState(item))
            },

            /**
             * Render line items template.
             */
            render () {
                axios.get('/cart.js')
                    .then((response) => {
                        if (response.data.item_count > 0) {
                            this.setActiveState(response.data)
                            this.updateKlarnaWidget(response.data)
                            return
                        }

                        this.setEmptyState()
                        this.updateKlarnaWidget(response.data)
                        throw new Error('Cart is empty')
                    })
                    .catch((error) => {
                        return error
                    })
                
                if (typeof nostojs !== "undefined") {
                    nostojs(function (api) {
                        var request = api.createRecommendationRequest()
                        request.addElements(['nosto-minicart'])
                        request.loadRecommendations()
                    })
                }
                
            },

            /**
             * Set active state.
             * @param {object} data - Cart data.
             */
            setActiveState (data) {
                this.setActiveTemplate(data.items)
                this.subtotal.innerHTML = formatMoney(data.total_price, window.theme.moneyFormat)
                this.footer.classList.remove(cssClasses.hidden)
                window.Frame.EventBus.emit('AjaxCart:reinit')
                theme.registerComponents(this.body)
            },

            /**
             * Update Klarna's widget.
             * @param {Object} data - Cart data.
             */
            updateKlarnaWidget (data) {
                if (!window.theme.klarnaMessaging) {
                    return
                }

                this.klarnaWidget.setAttribute('data-purchase-amount', data.total_price)
                window.KlarnaOnsiteService = window.KlarnaOnsiteService || []
                window.KlarnaOnsiteService.push({
                    eventName: 'refresh-placements'
                })
            },

            /**
             * Set loading state.
             * @param {object} element - DOM line item.
             */
            setLoadingState (element) {
                element.classList.add(cssClasses.removing)
            },

            /**
             * Set empty state.
             */
            setEmptyState () {
                this.body.innerHTML = this.getEmptyTemplate()
                this.footer.classList.add(cssClasses.hidden)
            },

            /**
             * Get cart drawer body template.
             * @param {Object} items - Cart line item object data.
             * @returns {string} - Template string.
             */
            setActiveTemplate (items) {
                let cartHTML = ''

                const freeGiftProductIds = this.freeGiftProducts.map(product => parseInt(product.id))
                const noneGiftItems = items.filter(item => !freeGiftProductIds.includes(item.id))
                const giftItems = items.filter(item => freeGiftProductIds.includes(item.id))

                noneGiftItems.forEach(item => { cartHTML += this.getLineItemTemplate(item) })
                giftItems.forEach(item => { cartHTML += this.getLineItemTemplate(item, true) })

                this.body.innerHTML = `${cartHTML}`
            },

            /**
             * Get empty cart template.
             */
            getEmptyTemplate () {
                return `<p>${window.theme.strings.cart.general.empty}</p>`
            },

            /**
             * Get line item template.
             * @param {Object} item - Cart line item object data.
             * @param {Bool} gift
             * @returns {string} - Template.
             */
            getLineItemTemplate (item, gift = false) {
                let partNumberHTML = ''

                if (item.product_type !== 'ESB') {
                    partNumberHTML = `<span class="ajax-cart__sku">${window.theme.strings.partNumber} ${item.sku}</span>`
                }

                return `
                <div class="ajax-cart__line-item${gift ? ' ajax-cart__line-item--gift' : ''}" js-ajax-cart="lineItem">
                  ${this.getImageHtml(item)}

                  <div class="ajax-cart__description">

                    <div class="ajax-cart__meta-wrapper">
                      <div class="ajax-cart__meta">
                        ${getProductTitle(item)}
                        ${partNumberHTML}
                      </div>

                      ${this.getVariantTitle(item)}

                      <div class="ajax-cart__controls text-right">
                        <div class="h4--gux ajax-cart__prices data-label="Price">
                          ${this.getPriceHtml(item)}
                        </div>
                      </div>
                    </div>

                    ${this.getESBProperties(item)}

                    ${this.getDeliveryDateHtml(item)}

                    <div class="ajax-cart__controls-wrapper">
                      <div class="ajax-cart__quantity">
                        <p class="ajax-cart__quantity-text weight--medium">${window.theme.strings.quantity}: ${item.quantity}</p>
                      </div>

                      <p><small class="ajax-cart__remove" js-ajax-cart="remove" data-key="${item.key}" tabindex="0">${window.theme.strings.cart.remove}</small></p>
                    </div>
                  </div>
                </div>
              `
            },

            /**
             * Return ESB properties html.
             * @param {Object} item The line item JSON object.
             * @returns {String} the item's ESc properties html.
             */
            getESBProperties (item) {
                let propertiesHtml = ''

                if (item.product_type !== 'ESB') return propertiesHtml

                propertiesHtml = `
                    <div class="cart-table__properties">
                        <h2 class="cart-table__property-title weight--medium">${window.theme.strings.servicePlans.cart.planDetails}</h2>

                        <span class="cart-table__property">
                            <span class="weight--medium">${window.theme.strings.servicePlans.cart.vinNumber}:</span> ${item.properties.vinNumber}</span>
                            <span class="cart-table__property"><span class="weight--medium">${window.theme.strings.servicePlans.cart.name}:</span> ${item.properties.name}</span>
                            <span class="cart-table__property"><span class="weight--medium">${window.theme.strings.servicePlans.cart.currentMileage}:</span> ${item.properties.currentMileage}</span>
                            <span class="cart-table__property"><span class="weight--medium">${window.theme.strings.servicePlans.cart.address}:</span> ${item.properties.address}</span>
                            <span class="cart-table__property"><span class="weight--medium">${window.theme.strings.servicePlans.cart.distance}:</span> ${item.properties.distance}</span>
                            <span class="cart-table__property"><span class="weight--medium">${window.theme.strings.servicePlans.cart.duration}:</span> ${item.properties.duration}</span>
                    </div>
                    `

                return propertiesHtml
            },

            /**
             * Return item's delivery date html.
             * @param {Object} item The line item JSON object.
             * @returns {String} the item's delivery date html.
             */
            getDeliveryDateHtml (item) {
                let deliveryHtml = ''
                // const deliveryDatesAttribute = item.product_type === this.section._selectors.lifestyleProduct ? `data-delivery-dates='{ "beforeCutOffDays": "${window.window.theme.deliveryDays.lifestyleDeliveryBeforeCutOff}", "afterCutOffDays": "${window.window.theme.deliveryDays.lifestyleDeliveryAfterCutOff}" }'` : `data-delivery-dates='{ "beforeCutOffDays": "${window.window.theme.deliveryDays.carPartsDeliveryBeforeCutOff}", "afterCutOffDays": "${window.window.theme.deliveryDays.carPartsDeliveryAfterCutOff}" }'`

                if ((item.properties !== null && item.properties._installation_bundle) || item.product_type === 'ESB' || item.product_type === 'with Fitting' || localStorage.getItem(`${item.handle}`) === null) {
                    deliveryHtml = ''
                } else {
                    deliveryHtml = `
                <div class="cart-table__estimated-delivery weight--medium">
                    ${window.theme.deliveryDays.deliveryTitle}: <span>${localStorage.getItem(`${item.handle}`)}</span>
                </div>
                `
                }

                return deliveryHtml
            },

            /**
             * Return the image object if it exists.
             * @param {Object} item The line item JSON object.
             * @returns {String} the image HTML element.
             */
            getImageHtml (item) {
                let imageHtml = ''

                if (item.image === null) {
                    return imageHtml
                }

                imageHtml = `
                  <div class="ajax-cart__image responsive-image__wrapper">
                    <img
                      src="${imageParameters(item.image, {size: '180x180', crop: 'center'})}"
                      srcset="${imageParameters(item.image, {size: '120x120', crop: 'center'})} 120w, ${imageParameters(item.image, {size: '240x240', crop: 'center'})} 240w, ${imageParameters(item.image, {size: '320x320', crop: 'center'})} 320w"
                      alt="${item.title}"
                    >
                    ${this.getInstallationBanner(item)}
                  </div>`

                return imageHtml
            },

            /**
             * Return installation banner.
             * @param {Object} item The line item JSON object.
             * @returns {String} installation HTML.
             */
            getInstallationBanner (item) {
                let installationBanner = ''

                if (item.properties === null) {
                    return installationBanner
                }

                if (item.properties._installation_bundle === 'true') {
                    installationBanner = `<div class="ajax-cart__banner">${window.theme.strings.cart.drawer.installationIncluded}</div>`
                }

                return installationBanner
            },

            /**
             * Return price HTML based on parameters.
             * @param {Object} item The line item JSON object.
             * @returns {String} Price HTML.
             */
            getPriceHtml (item) {
                let priceHtml = `<span class="price">${formatMoney(item.price, window.theme.moneyFormat)}</span>`

                if (item.properties === null) {
                    return priceHtml
                }

                if (item.properties._compare_at_price) {
                    const percentageOff = percentageSaving(item.price, item.properties._compare_at_price)
                    let discountHtml = `<span class="visually-hidden">${window.theme.strings.originalPrice}</span>
                    <span class="price price--compare-at"><s>${formatMoney(item.properties._compare_at_price, window.theme.moneyFormat)}</s></span>
                    <span class="price price--percentage-off">(${percentageOff}${window.theme.strings.percentageOff})</span>`

                    if (percentageOff <= 0) discountHtml = ''

                    priceHtml = `
                    <span class="visually-hidden">${window.theme.strings.discountedPrice}</span>
                    <span class="price">${formatMoney(item.price, window.theme.moneyFormat)}</span>
                    ${discountHtml}`
                }

                return priceHtml
            },

            /**
             * Get variant titles.
             * @param {object} item - Line item data object.
             * @returns {String} Variant title HTML.
             */
            getVariantTitle (item) {
                if (item.variant_options.length && !item.variant_options[0].includes('Default Title')) {
                    let variantTitle = this._checkForDefaultVariant(item.variant_title)
                    return `<p class="meta">${variantTitle}</p>`
                } else {
                    return ''
                }
            },

            /**
             * Check for default variant titles.
             * @param {object} title - Variant title.
             * @returns {String} Variant class.
             */
            _checkForDefaultVariant (title) {
                if (title.includes('DEFAULT: ')) {
                    title = title.split('DEFAULT: ')[1]
                }
                return title
            },

            /**
             * Open drawer and force render.
             */
            open () {
                this._Drawer._open()
                this.render()
                this.showBanner()
            },

            /**
             * Open mobile-only AOC banner.
             */
            showBanner () {
                this.aocBanner.classList.add(cssClasses.active)
                this.aocBanner.parentNode.parentNode.classList.remove(cssClasses.overflow)

                window.setTimeout(() => {
                    this.aocBanner.classList.remove(cssClasses.active)
                }, 3000)

                window.setTimeout(() => {
                    this.aocBanner.parentNode.parentNode.classList.add(cssClasses.overflow)
                }, 3200)
            },

            /**
             * Callback function when section is loaded via 'sections.load()' or by the Theme Editor 'shopify:section:load' event.
             */
            onLoad () {
                this.init()
                this.setListeners()
            }
        })
    }
}

export default CartDrawer



// WEBPACK FOOTER //
// ./src/resources/scripts/sections/CartDrawer.js